import { useEffect, useRef, useState } from 'react';

/**
 * Returns true when a transient pulse animation class should be applied.
 *
 * The hook ignores the first render (no animation on initial mount) and
 * any render where pulseKey is undefined. When pulseKey transitions to a
 * new defined value after the first render, isPulsing becomes true for
 * durationMs, then resets. The timer is cleared on unmount or when
 * pulseKey changes again, preventing stale state writes.
 */
export function usePulseClass(
  pulseKey: number | undefined,
  durationMs: number = 1000
): boolean {
  const [isPulsing, setIsPulsing] = useState(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (pulseKey === undefined) {
      setIsPulsing(false);
      return;
    }
    setIsPulsing(true);
    const timer = setTimeout(() => setIsPulsing(false), durationMs);
    return () => clearTimeout(timer);
  }, [pulseKey, durationMs]);

  return isPulsing;
}
